import React, { useEffect, useMemo, useState } from "react";
import FoodReportSelection from "../components/FoodReportSelection";
import {
  fetchFoodReportTransactions,
  fetchFoodReportPreferences,
  saveIncludedFoods,
  saveFoodOrderCost,
} from "../services/foodReport";
import {
  buildFoodOrderRows,
  foodReportCSV,
  parseFoodCost,
  getFoodReportNames,
  suggestIncludedFoods,
  summarizeFoodOrders,
} from "../utils/foodReport";
import "../css/FoodReport.css";

const money = (value) =>
  value === null
    ? "—"
    : new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
      }).format(value);
const PAGE_SIZE = 25;

function FoodCostRow({ row, onSave, busy, saving }) {
  const [draft, setDraft] = useState(
    row.cost === null ? "" : row.cost.toFixed(2),
  );
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);
  useEffect(() => {
    setDraft(row.cost === null ? "" : row.cost.toFixed(2));
  }, [row.cost]);

  let parsedCost;
  try {
    parsedCost = parseFoodCost(draft);
  } catch {
    parsedCost = undefined;
  }
  const changed = parsedCost !== row.cost;
  const save = async (event) => {
    event.preventDefault();
    if (busy || !changed) return;
    setError("");
    setSaved(false);
    try {
      parseFoodCost(draft);
      await onSave(row, draft);
      setSaved(true);
    } catch (err) {
      setError(err.message || "Could not save the cost. Please try again.");
    }
  };

  return (
    <tr>
      <td>
        {row.date || "Date not recorded"}
        <span className="food-detail">{row.time}</span>
      </td>
      <td>{row.customer}</td>
      <td>{row.food}</td>
      <td className="td-right">
        {row.revenue === null ? "Not recorded" : money(row.revenue)}
      </td>
      <td>
        <form className="food-cost-form" onSubmit={save} noValidate>
          <div className="food-cost-inputs">
            <span aria-hidden="true">$</span>
            <input
              aria-label={`My cost for ${row.food}, ${row.customer}, ${row.date}, ${row.time}`}
              aria-describedby={error ? `cost-error-${row.id}` : undefined}
              aria-invalid={Boolean(error)}
              type="text"
              inputMode="decimal"
              placeholder="Not entered"
              value={draft}
              disabled={saving}
              onChange={(event) => {
                setDraft(event.target.value);
                setError("");
                setSaved(false);
              }}
            />
            <button
              type="submit"
              className="btn food-save"
              disabled={busy || !changed}
            >
              {saving ? "Saving…" : "Save"}
            </button>
          </div>
          {changed && !saving && <span className="food-detail">Unsaved</span>}
          {saved && !changed && (
            <span className="food-saved" role="status">
              Saved
            </span>
          )}
          {error && (
            <span
              className="food-error"
              id={`cost-error-${row.id}`}
              role="alert"
            >
              {error}
            </span>
          )}
        </form>
      </td>
      <td className={`td-right ${row.profit < 0 ? "food-loss" : ""}`}>
        {row.cost === null ? (
          <span className="food-missing">Needs cost</span>
        ) : row.revenue === null ? (
          "Missing sale amount"
        ) : (
          money(row.profit)
        )}
      </td>
    </tr>
  );
}

const FoodReport = () => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [period, setPeriod] = useState("thisMonth");
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  });
  const [refresh, setRefresh] = useState(0);
  const [foodItems, setFoodItems] = useState([]);
  const [includedFoods, setIncludedFoods] = useState(null);
  const [selectingFoods, setSelectingFoods] = useState(false);
  const [savingSelection, setSavingSelection] = useState(false);
  const [search, setSearch] = useState("");
  const [costFilter, setCostFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [savingId, setSavingId] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const fetchData = async () => {
      setLoading(true);
      setError("");
      setTransactions([]);
      try {
        const [data, preferences] = await Promise.all([
          fetchFoodReportTransactions(period, selectedMonth),
          fetchFoodReportPreferences(),
        ]);
        if (!cancelled) {
          setTransactions(data);
          setFoodItems(preferences.foodItems);
          setIncludedFoods(preferences.includedFoods);
        }
      } catch (err) {
        if (!cancelled)
          setError("Could not load food orders. Please try again.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchData();
    return () => {
      cancelled = true;
    };
  }, [period, selectedMonth, refresh]);

  useEffect(() => {
    setPage(1);
  }, [period, selectedMonth, search, costFilter]);

  const foodNames = useMemo(
    () =>
      Array.from(
        new Set([
          ...getFoodReportNames(transactions, foodItems),
          ...(includedFoods || []),
        ]),
      ).sort((a, b) => a.localeCompare(b)),
    [transactions, foodItems, includedFoods],
  );
  const selectedFoods = useMemo(
    () => includedFoods ?? suggestIncludedFoods(foodNames),
    [includedFoods, foodNames],
  );
  const rows = useMemo(
    () => buildFoodOrderRows(transactions, selectedFoods),
    [transactions, selectedFoods],
  );
  const totals = useMemo(() => summarizeFoodOrders(rows), [rows]);
  const matchingRows = useMemo(() => {
    const term = search.trim().toLowerCase();
    return rows.filter(
      (row) =>
        (!term ||
          `${row.food} ${row.customer} ${row.date}`
            .toLowerCase()
            .includes(term)) &&
        (costFilter === "all" || row.cost === null),
    );
  }, [rows, search, costFilter]);
  const pageCount = Math.max(1, Math.ceil(matchingRows.length / PAGE_SIZE));
  const currentPage = Math.min(page, pageCount);
  const visibleRows = matchingRows.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE,
  );
  const busy = savingId !== null || savingSelection;
  const periodLabel =
    period === "allTime"
      ? "All time"
      : period === "thisYear"
        ? "This year"
        : period === "thisMonth"
          ? "This month"
          : selectedMonth
            ? new Date(`${selectedMonth}-01T12:00:00`).toLocaleDateString(
                "en-US",
                { month: "long", year: "numeric" },
              )
            : "Selected month";

  const saveCost = async (row, value) => {
    setSavingId(row.id);
    try {
      const updated = await saveFoodOrderCost(row, value, selectedFoods);
      setTransactions((previous) =>
        previous.map((item) => (item.id === updated.id ? updated : item)),
      );
    } catch (err) {
      if (err.code)
        throw new Error(
          "Could not save the cost. Check your connection and try again.",
        );
      throw err;
    } finally {
      setSavingId(null);
    }
  };

  const saveSelection = async (names) => {
    setSavingSelection(true);
    try {
      await saveIncludedFoods(names);
      setIncludedFoods(names);
      setSelectingFoods(false);
      setPage(1);
    } finally {
      setSavingSelection(false);
    }
  };

  const exportCSV = () => {
    const headers = [
      "Transaction ID",
      "Order ID",
      "Date",
      "Time",
      "Customer",
      "Order",
      "Customer charged (before tax)",
      "My cost (whole order)",
      "Gross profit",
      "Cost status",
    ];
    const values = matchingRows.map((row) => [
      row.transactionId,
      row.orderId,
      row.date,
      row.time,
      row.customer,
      row.food,
      row.revenue,
      row.cost,
      row.profit,
      row.cost === null ? "Needs cost" : "Recorded",
    ]);
    const blob = new Blob([foodReportCSV(headers, values)], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `food_orders_${period === "month" ? selectedMonth : period}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="food-report">
      <div className="food-report-header">
        <h2>Food Sales & Profit</h2>
        <p className="food-description">
          One total cost and profit for each outside food order, using only your
          included foods.
        </p>
        <div className="controls-row">
          <div className="control">
            <label htmlFor="food-period">Period</label>
            <select
              id="food-period"
              value={period}
              disabled={busy || selectingFoods}
              onChange={(event) => setPeriod(event.target.value)}
            >
              <option value="thisMonth">This Month</option>
              <option value="month">Choose Month</option>
              <option value="thisYear">This Year</option>
              <option value="allTime">All Time</option>
            </select>
          </div>
          {period === "month" && (
            <div className="control">
              <label htmlFor="food-month">Month</label>
              <input
                id="food-month"
                type="month"
                min="1900-01"
                max="9999-12"
                value={selectedMonth}
                disabled={busy || selectingFoods}
                onChange={(event) => setSelectedMonth(event.target.value)}
              />
            </div>
          )}
          <button
            className="btn food-secondary"
            disabled={busy || selectingFoods || loading}
            onClick={() => setRefresh((value) => value + 1)}
          >
            Refresh
          </button>
          <button
            className="btn btn-export"
            disabled={
              busy ||
              selectingFoods ||
              loading ||
              Boolean(error) ||
              !matchingRows.length
            }
            onClick={exportCSV}
          >
            Export CSV
          </button>
        </div>
      </div>

      {loading ? (
        <p role="status">Loading food orders…</p>
      ) : error ? (
        <div className="food-load-error" role="alert">
          <p>{error}</p>
          <button
            className="btn food-secondary"
            onClick={() => setRefresh((value) => value + 1)}
          >
            Try again
          </button>
        </div>
      ) : (
        <>
          <div className="food-included-summary">
            <p className="food-description">
              {includedFoods === null
                ? "Starting with pizza, chicken, and salad. Choose any other foods you buy from outside."
                : `${selectedFoods.length} foods included. Unselected items are excluded from orders and totals.`}
            </p>
            <button
              className="btn food-secondary"
              disabled={busy || selectingFoods}
              onClick={() => setSelectingFoods(true)}
            >
              Included foods
            </button>
          </div>
          {selectingFoods && (
            <FoodReportSelection
              names={foodNames}
              selected={selectedFoods}
              onSave={saveSelection}
              onCancel={() => setSelectingFoods(false)}
              busy={busy}
            />
          )}
          <h3 className="food-period-heading">{periodLabel} totals</h3>
          <div className="kpis">
            <div className="kpi">
              <div className="kpi-label">Food sales</div>
              <div className="kpi-value">{money(totals.revenue)}</div>
              <span className="food-detail">
                Before customer tax and card fees
              </span>
            </div>
            <div className="kpi">
              <div className="kpi-label">My recorded costs</div>
              <div className="kpi-value">{money(totals.cost)}</div>
              <span className="food-detail">Supplier costs you have saved</span>
            </div>
            <div className="kpi">
              <div className="kpi-label">
                Gross profit
                {totals.missingCosts || totals.missingRevenue
                  ? " (partial)"
                  : ""}
              </div>
              <div
                className={`kpi-value ${totals.profit < 0 ? "food-loss" : ""}`}
              >
                {money(totals.profit)}
              </div>
              <span className="food-detail">
                {totals.profitLines} of {totals.lineCount} orders included
                {totals.margin === null
                  ? ""
                  : ` · ${totals.margin.toFixed(1)}% margin`}
              </span>
            </div>
            <div className="kpi">
              <div className="kpi-label">Orders needing cost</div>
              <div className="kpi-value">{totals.missingCosts}</div>
              <span className="food-detail">
                {totals.orderCount} orders · {totals.quantity} items sold
              </span>
            </div>
          </div>
          <p className="food-description">
            Gross profit = food sales − your food costs, before other business
            expenses. Only orders with both a sale amount and a saved cost count
            toward profit. Totals cover the entire period.
          </p>
          {totals.missingRevenue > 0 && (
            <p className="food-notice">
              {totals.missingRevenue} orders have an incomplete sale amount and
              are excluded from sales and profit totals.
            </p>
          )}

          <div className="controls-row">
            <div className="control food-search">
              <label htmlFor="food-search">Search orders</label>
              <input
                id="food-search"
                type="search"
                placeholder="Food, customer, or date"
                value={search}
                disabled={busy || selectingFoods}
                onChange={(event) => setSearch(event.target.value)}
              />
            </div>
            <div className="control">
              <label htmlFor="food-cost-filter">Costs</label>
              <select
                id="food-cost-filter"
                value={costFilter}
                disabled={busy || selectingFoods}
                onChange={(event) => setCostFilter(event.target.value)}
              >
                <option value="all">All orders</option>
                <option value="missing">Needs cost</option>
              </select>
            </div>
          </div>

          <>
            <p className="food-description food-cost-help">
              Enter the <strong>total you paid for the whole food order</strong>
              , including any supplier tax or delivery you want to count, then
              click Save. For example, 2 pizzas + 1 salad appear together with
              one cost and one profit. Include only the foods shown in the
              order. Enter 0 for no cost; clear and save to mark a cost as
              unknown.
            </p>
            {rows.some((row) => row.legacy) && (
              <p className="food-description">
                Older orders are grouped by the saved customer transaction
                because separate submission details were not recorded.
              </p>
            )}
            <div className="table-wrapper">
              <table className="transaction-table food-orders-table">
                <caption className="food-table-caption">
                  {matchingRows.length} matching food orders · newest first
                </caption>
                <thead>
                  <tr>
                    <th scope="col">Date</th>
                    <th scope="col">Customer</th>
                    <th scope="col">Order</th>
                    <th scope="col" className="td-right">
                      Customer charged
                      <span className="food-detail">Before tax / fees</span>
                    </th>
                    <th scope="col">
                      My cost
                      <span className="food-detail">Whole food order</span>
                    </th>
                    <th scope="col" className="td-right">
                      Gross profit
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {visibleRows.length ? (
                    visibleRows.map((row) => (
                      <FoodCostRow
                        key={`${row.id}:${row.itemsSignature}`}
                        row={row}
                        onSave={saveCost}
                        busy={busy || selectingFoods}
                        saving={savingId === row.id}
                      />
                    ))
                  ) : (
                    <tr>
                      <td colSpan="6">
                        {rows.length
                          ? "No food orders match these filters."
                          : "No food orders for this period."}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            {pageCount > 1 && (
              <div className="food-pagination">
                <button
                  className="btn food-secondary"
                  disabled={busy || selectingFoods || currentPage === 1}
                  onClick={() => setPage(currentPage - 1)}
                >
                  Previous
                </button>
                <span>
                  Page {currentPage} of {pageCount}
                </span>
                <button
                  className="btn food-secondary"
                  disabled={busy || selectingFoods || currentPage === pageCount}
                  onClick={() => setPage(currentPage + 1)}
                >
                  Next
                </button>
              </div>
            )}
          </>
        </>
      )}
    </div>
  );
};

export default FoodReport;
